import { defineStore, storeToRefs } from 'pinia'
import { ref, computed } from 'vue'
import { useConfigStore } from './configStore'
import { useAuthStore } from './authStore'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import api from '@/services/axiosApi'

const usfUrl = import.meta.env.VITE_USF_URL

export type MediaScope = 'team' | 'user'

export const useMediaStore = defineStore('media', () => {
  const configStore = useConfigStore()
  const authStore = useAuthStore()
  const { currentTeamPid } = storeToRefs(configStore)
  const { user } = storeToRefs(authStore)
  const queryClient = useQueryClient()

  /**
   * Get the path prefix for the given scope
   */
  const getPathPrefix = (scope: MediaScope = 'team'): string => {
    if (scope === 'user') {
      return `u-${user.value?.id}`
    }
    return currentTeamPid.value || ''
  }

  /**
   * Get the API endpoint for the given scope
   */
  const getEndpoint = (scope: MediaScope = 'team'): string => {
    if (scope === 'user') {
      return '/api/user/files'
    }
    return `/api/${currentTeamPid.value}/files`
  }

  // Getters
  /**
   * Get the external URL for a given file path
   * @param url - The file path or USF URL
   * @returns The complete external URL
   */
  const getExternalUrl = computed(() => (url: string) => {
    if (url.includes('usf://')) {
      // Extract the path after usf://
      const pathAfterProtocol = url.replace('usf://', '')

      // Check if there's a path segment (e.g., "teamPid/hash.jpg" or "u-123/hash.jpg")
      // vs legacy format that's just the hash directly (e.g., "hash.jpg")
      if (pathAfterProtocol.includes('/')) {
        // New format: already has the path segment (teamPid or u-userId)
        return `${usfUrl}/${pathAfterProtocol}`
      } else {
        // Legacy format: just the hash, need to inject team PID
        return `${usfUrl}/${currentTeamPid.value}/${pathAfterProtocol}`
      }
    } else {
      // Legacy: just a hash (no usf:// prefix), assume team-scoped
      return `${usfUrl}/${currentTeamPid.value}/${url}`
    }
  })

  // Team-scoped queries (default, for backward compatibility)
  const mediaQuery = useQuery({
    queryKey: ['media', 'team', currentTeamPid],
    queryFn: () =>
      api.get(`/api/${currentTeamPid.value}/files`).then((res) => res.data),
    enabled: computed(() => !!currentTeamPid.value)
  })

  // User-scoped queries
  const userMediaQuery = useQuery({
    queryKey: ['media', 'user', computed(() => user.value?.id)],
    queryFn: () => api.get('/api/user/files').then((res) => res.data),
    enabled: computed(() => !!user.value?.id)
  })

  const allMedia = computed(() => mediaQuery.data.value || [])
  const allUserMedia = computed(() => userMediaQuery.data.value || [])

  /**
   * Get media list for the given scope
   */
  const getMedia = (scope: MediaScope = 'team') => {
    return scope === 'user' ? allUserMedia.value : allMedia.value
  }

  /**
   * Check if media is loading for the given scope
   */
  const isLoadingMedia = (scope: MediaScope = 'team') => {
    return scope === 'user' ? userMediaQuery.isLoading.value : mediaQuery.isLoading.value
  }

  // Team-scoped mutations
  const uploadMediaMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return api.post(`/api/${currentTeamPid.value}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['media', 'team', currentTeamPid]
      })
    }
  })

  // User-scoped mutations
  const uploadUserMediaMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return api.post('/api/user/files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['media', 'user', user.value?.id]
      })
    }
  })

  const deleteMediaMutation = useMutation({
    mutationFn: (hash: string) =>
      api.delete(`/api/${currentTeamPid.value}/files/${hash}`),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['media', 'team', currentTeamPid]
      })
    }
  })

  const deleteUserMediaMutation = useMutation({
    mutationFn: (hash: string) => api.delete(`/api/user/files/${hash}`),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['media', 'user', user.value?.id]
      })
    }
  })

  const uploadFromUrlMutation = useMutation({
    mutationFn: (url: string) =>
      api.post<{ hashName: string }>(`/api/${currentTeamPid.value}/files/from-url`, { url }),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['media', 'team', currentTeamPid]
      })
    }
  })

  /**
   * Upload a new media file
   * @param file - The file to upload
   * @param scope - 'team' (default) or 'user'
   */
  function uploadMedia(file: File, scope: MediaScope = 'team') {
    if (file.size > 2 * 1024 * 1024) {
      const notificationStore = useNotificationStore()
      notificationStore.addError('File size must be less than 2MB')
      return
    }

    if (scope === 'user') {
      return uploadUserMediaMutation.mutate(file)
    }
    return uploadMediaMutation.mutate(file)
  }

  /**
   * Delete a media file
   * @param hash - The hash of the file to delete
   * @param scope - 'team' (default) or 'user'
   */
  function deleteMedia(hash: string, scope: MediaScope = 'team') {
    if (scope === 'user') {
      return deleteUserMediaMutation.mutate(hash)
    }
    return deleteMediaMutation.mutate(hash)
  }

  /**
   * Upload a media file from an external URL (sideload).
   * Downloads the image server-side and stores it in the team's media library.
   * Note: Only supported for team scope currently.
   * @param url - The external URL of the image to upload
   * @returns Promise that resolves to the usf:// formatted URL
   */
  async function uploadMediaFromUrl(url: string): Promise<string> {
    const response = await uploadFromUrlMutation.mutateAsync(url)
    return `usf://${currentTeamPid.value}/${response.data.hashName}`
  }

  /**
   * Build a USF URL for a given hash and scope
   * @param hash - The file hash
   * @param scope - 'team' (default) or 'user'
   */
  function buildUsfUrl(hash: string, scope: MediaScope = 'team'): string {
    const prefix = getPathPrefix(scope)
    return `usf://${prefix}/${hash}`
  }

  /**
   * Check if uploading media for the given scope
   */
  const isUploadingMedia = (scope: MediaScope = 'team') => {
    return scope === 'user'
      ? uploadUserMediaMutation.isPending.value
      : uploadMediaMutation.isPending.value
  }

  /**
   * Check if deleting media for the given scope
   */
  const isDeletingMedia = (scope: MediaScope = 'team') => {
    return scope === 'user'
      ? deleteUserMediaMutation.isPending.value
      : deleteMediaMutation.isPending.value
  }

  return {
    // URL helpers
    getExternalUrl,
    buildUsfUrl,
    getPathPrefix,

    // Media data (backward compatible)
    allMedia,
    allUserMedia,
    getMedia,
    isLoadingMedia,

    // Actions (scope-aware)
    uploadMedia,
    deleteMedia,
    uploadMediaFromUrl,

    // Loading states (backward compatible)
    isLoading: mediaQuery.isLoading,
    isError: mediaQuery.isError,
    isUploading: uploadMediaMutation.isPending,
    isUploadingFromUrl: uploadFromUrlMutation.isPending,
    isDeleting: deleteMediaMutation.isPending,

    // Scope-aware loading states
    isUploadingMedia,
    isDeletingMedia
  }
})
